
import React from "react";
import { FilePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FloorPlanCard } from "./FloorPlanCard";
import { Checkbox } from "@/components/ui/checkbox";

interface FloorPlan {
  id: string;
  name: string;
  updated_at: string;
  content: any;
  preview_image?: string | null;
  is_active: boolean;
  folder_id?: string | null;
  is_favorite?: boolean;
  is_public?: boolean;
}

interface FloorPlansListProps {
  loading: boolean;
  floorPlans: FloorPlan[];
  editingName: string | null;
  onEditName: (id: string | null) => void;
  onUpdateName: (id: string, newName: string) => void;
  onDuplicate: (plan: FloorPlan) => void;
  onDelete: (id: string) => void;
  onNewPlan?: () => void;
  onUpdateFolder: (id: string, folderId: string | null) => void;
  onToggleFavorite: (id: string, favorite: boolean) => void;
  onToggleVisibility?: (id: string, isPublic: boolean) => void;
  selectedPlans?: string[];
  onSelectPlan?: (id: string) => void;
}

export function FloorPlansList({
  loading,
  floorPlans,
  editingName,
  onEditName,
  onUpdateName,
  onDuplicate,
  onDelete,
  onNewPlan,
  onUpdateFolder,
  onToggleFavorite,
  onToggleVisibility,
  selectedPlans = [],
  onSelectPlan,
}: FloorPlansListProps) {
  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Laden...
      </div>
    );
  }

  if (!Array.isArray(floorPlans)) {
    return (
      <div className="text-center py-8 text-red-600">
        Er is een fout opgetreden bij het laden van de plattegronden.
      </div>
    );
  }

  if (floorPlans.length === 0) {
    return (
      <Card className="p-8 text-center">
        <CardContent className="space-y-4 pt-4">
          <div className="flex flex-col items-center gap-4">
            <p className="text-muted-foreground">
              Je hebt nog geen plattegronden. Maak er een aan om te beginnen!
            </p>
            {onNewPlan && (
              <Button onClick={onNewPlan} className="gap-2">
                <FilePlus className="h-4 w-4" />
                Nieuwe plattegrond
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {floorPlans.map((plan) => (
        <div key={plan.id}>
          <FloorPlanCard
            plan={plan}
            editingName={editingName}
            onEditName={onEditName}
            onUpdateName={onUpdateName}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onUpdateFolder={onUpdateFolder}
            onToggleFavorite={onToggleFavorite}
            onToggleVisibility={onToggleVisibility}
            showCheckbox={!!onSelectPlan}
            isSelected={selectedPlans?.includes(plan.id)}
            onSelect={() => onSelectPlan?.(plan.id)}
          />
        </div>
      ))}
    </div>
  );
}
